import { useMemo, useState } from 'react';
import { CheckCircle2, Eye, GitMerge, Sparkles, Trash2 } from 'lucide-react';

import { ErrorState } from '@/components/ui/ErrorState';
import { Spinner } from '@/components/ui/Spinner';
import { KpiCard, formatNumber } from '@/features/dashboard/widgets';
import { ClusterCard } from '@/features/mdm/components/ClusterCard';
import {
  useClearClusters,
  useClusters,
  useReviewCluster,
  useUpdateGolden,
} from '@/features/mdm/hooks';
import type { ClusterStatus } from '@/features/mdm/types';
import type { ApiError } from '@/lib/errors';

const STATUS_FILTERS: Array<{ value: ClusterStatus | 'all'; label: string }> = [
  { value: 'open', label: 'Needs review' },
  { value: 'approved', label: 'Confirmed' },
  { value: 'rejected', label: 'Not a match' },
  { value: 'all', label: 'All' },
];

const CLEAR_LABEL: Record<ClusterStatus | 'all', string> = {
  open: 'needs-review',
  approved: 'confirmed',
  rejected: 'not-a-match',
  all: 'all',
};

export function MdmPage(): JSX.Element {
  const [statusFilter, setStatusFilter] = useState<ClusterStatus | 'all'>('open');

  const clusters = useClusters({ status: statusFilter === 'all' ? undefined : statusFilter });
  const allClusters = useClusters({});
  const review = useReviewCluster();
  const saveGolden = useUpdateGolden();
  const clearAll = useClearClusters();

  // Counts for the summary cards (always the full, unfiltered set).
  const stats = useMemo(() => {
    const all = allClusters.data ?? [];
    const scored = all.filter((c) => c.avg_score !== null);
    const avg =
      scored.length > 0 ? scored.reduce((s, c) => s + (c.avg_score ?? 0), 0) / scored.length : 0;
    return {
      total: all.length,
      open: all.filter((c) => c.status === 'open').length,
      approved: all.filter((c) => c.status === 'approved').length,
      avgSimilarity: Math.round(avg * 100),
    };
  }, [allClusters.data]);

  function handleClearAll(): void {
    const scope = statusFilter === 'all' ? 'all' : `${statusFilter}`;
    if (window.confirm(`Delete ${scope} match group(s)? This cannot be undone.`)) {
      clearAll.mutate(statusFilter === 'all' ? undefined : statusFilter);
    }
  }

  const busyId = review.isPending
    ? review.variables.id
    : saveGolden.isPending
      ? saveGolden.variables.id
      : null;

  return (
    <div className="space-y-7">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow eyebrow-dot mb-2.5">
            <span>MODULE</span>
          </div>
          <h1 className="h-display">Master Data Management</h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-ink-500 dark:text-ink-400">
            We&rsquo;ve grouped records that look like the same thing (the same customer, claim,
            product…). Open each group, check the combined details, then tell us whether it&rsquo;s
            really a match.
          </p>
        </div>
        {clusters.data && clusters.data.length > 0 && (
          <button
            type="button"
            disabled={clearAll.isPending}
            onClick={handleClearAll}
            className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg border border-accent-crimson/30 bg-white dark:bg-ink-900 px-3 text-xs font-medium text-accent-crimson transition hover:-translate-y-px hover:bg-accent-crimson/5 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Trash2 className="h-3 w-3" />
            {clearAll.isPending ? 'Clearing…' : `Clear ${CLEAR_LABEL[statusFilter]}`}
          </button>
        )}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard
          label="Match Groups"
          value={formatNumber(stats.total)}
          sub="sets of likely duplicates"
          icon={GitMerge}
          accent="violet"
        />
        <KpiCard
          label="Needs Review"
          value={formatNumber(stats.open)}
          sub="waiting for your decision"
          icon={Eye}
          accent="crimson"
          alert={stats.open > 0}
        />
        <KpiCard
          label="Confirmed"
          value={formatNumber(stats.approved)}
          sub="merged into one record"
          icon={CheckCircle2}
          accent="brand"
        />
        <KpiCard
          label="Avg Similarity"
          value={`${stats.avgSimilarity}%`}
          sub="how alike grouped records are"
          icon={Sparkles}
          accent="sky"
        />
      </div>

      <section className="rounded-xl border border-ink-200/70 dark:border-white/10 bg-white dark:bg-ink-900 shadow-soft animate-slide-up">
        <header className="flex items-start gap-3 border-b border-ink-100 dark:border-white/5 px-6 py-4">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-violet/10 text-accent-violet">
            <GitMerge className="h-4 w-4" />
          </div>
          <div>
            <h3 className="h-section">Groups to review</h3>
            <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
              Each card is a set of records we think describe the same thing.
            </p>
          </div>
        </header>
        <div className="p-6">
          <div className="mb-4 inline-flex flex-wrap items-center gap-1 rounded-lg border border-ink-200/70 dark:border-white/10 bg-ink-50/60 dark:bg-white/[0.03] p-1">
            {STATUS_FILTERS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => setStatusFilter(value)}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                  statusFilter === value
                    ? 'bg-white dark:bg-ink-900 text-ink-900 dark:text-ink-50 shadow-soft ring-1 ring-ink-200/70'
                    : 'text-ink-500 dark:text-ink-400 hover:text-ink-800'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {clusters.isPending ? (
            <Spinner />
          ) : clusters.isError ? (
            <ErrorState error={clusters.error as ApiError} />
          ) : clusters.data.length === 0 ? (
            <div className="rounded-xl border border-dashed border-ink-300 dark:border-white/15 bg-ink-50/40 dark:bg-white/[0.02] p-8 text-center">
              <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 dark:bg-brand-900/30 text-brand-600">
                <GitMerge className="h-4 w-4" />
              </div>
              <p className="mt-3 text-sm text-ink-600 dark:text-ink-300">
                Nothing here yet. To find duplicates, go to{' '}
                <strong className="text-ink-900 dark:text-ink-50">Data Sources</strong> → open a dataset →{' '}
                <strong className="text-ink-900 dark:text-ink-50">Find duplicates</strong>.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {clusters.data.map((cluster) => (
                <ClusterCard
                  key={cluster.id}
                  cluster={cluster}
                  busy={busyId === cluster.id}
                  onSaveGolden={(id, golden) => saveGolden.mutate({ id, golden })}
                  onApprove={(id) => review.mutate({ id, action: 'approve' })}
                  onReject={(id) => review.mutate({ id, action: 'reject' })}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
